import {
    useMatches,
    useRouteError,
    isRouteErrorResponse,
    useLocation,
} from 'react-router';
type HeaderTheme = 'default' | 'transparent' | 'none' | 'app';
type HeaderSolidTheme = 'light' | 'dark';

interface LoaderData {
    pageTheme?: HeaderTheme;
    /** Scrolled-solid header theme — dark editorial pages ask for a near-black
     *  bar with light icons. Read from the loader (not the static route handle)
     *  so a route serving both a dark and a light variant can gate it. */
    headerSolidTheme?: HeaderSolidTheme;
}

// Type guard to ensure the loader data has the correct shape
const isLoaderData = (data: unknown): data is LoaderData => {
    return (
        typeof data === 'object' &&
        data !== null &&
        'pageTheme' in data &&
        typeof (data as LoaderData).pageTheme === 'string'
    );
};

export const usePageTheme = (): HeaderTheme => {
    const matches = useMatches();
    const error = useRouteError();
    const isError = isRouteErrorResponse(error);
    const location = useLocation();

    // If there's an error, return transparent theme
    if (isError) {
        return 'transparent';
    }

    // Iterate over matches in reverse to find the first match with pageTheme
    for (let i = matches.length - 1; i >= 0; i--) {
        const match = matches[i];

        // Ensure match exists and has the required pageTheme data
        if (match && isLoaderData(match.data) && match.data.pageTheme) {
            const theme = match.data.pageTheme;

            // Product pages use the default (solid black) navbar, with two
            // exceptions gated on the matched route id (not just the pathname):
            // - the catch-all 404 route (`routes/_app.$`) at a /product-prefixed
            //   not-found URL (e.g. /products-typo) keeps the transparent overlay
            //   header its loader asked for instead of being forced solid; and
            // - the product detail route's own loader may opt in (the mobile
            //   revamp's full-bleed gallery, BC-4524).
            if (
                location.pathname.startsWith('/product') &&
                match.id.includes('product') &&
                !match.id.includes('product.$urlSegment')
            ) {
                return 'default';
            }

            return theme;
        }
    }

    return 'default';
};

/**
 * The scrolled-solid header theme the deepest matched loader asks for (e.g. the
 * project detail revamp's dark bar), or undefined to fall back to the route
 * handle's `solidTheme`. Loader-driven so a route that renders both a dark and a
 * light variant behind a flag can opt in per render.
 */
export const useHeaderSolidTheme = (): HeaderSolidTheme | undefined => {
    const matches = useMatches();

    for (let i = matches.length - 1; i >= 0; i--) {
        const data = matches[i]?.data;
        if (data && typeof data === 'object' && 'headerSolidTheme' in data) {
            const theme = (data as LoaderData).headerSolidTheme;
            if (theme === 'light' || theme === 'dark') return theme;
        }
    }

    return undefined;
};
